import { useEffect, useState } from 'react';
import { FaTimes, FaTrash } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';

import { ImageStatus, removeImage, selectAllImageQueue } from '../../Store/imageQueueSlice';
import { RootState } from '../../Store/store';
import sizeToTxt from '../../utils/sizeToText';

interface HookFormImageUploadProps {
  key: number;
  status: ImageStatus;
  id: number;
}

function HookFormImageUpload({ key, status, id }: HookFormImageUploadProps) {
  const dispatch = useDispatch();
  const [prog, setProg] = useState(0);
  const image = useSelector((state: RootState) => selectAllImageQueue(state)[status].find((img) => img.imageId === id));
  const hasError = !!image?.errorMsg?.length;
  useEffect(() => {
    const progress = image?.progress || 0;
    if (prog < progress) setProg(progress);
  }, [image?.progress, prog]);

  const handleDelete = () => {
    dispatch(removeImage({ id: id, status: status }));
  };

  return (
    <li className="relative py-3 px-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150">
      <div className="flex items-center justify-between">
        <div className="flex min-w-0 flex-1 items-center">
          <div className="min-w-0 flex-1">
            <p className={`truncate text-sm font-medium ${hasError ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-gray-100"}`}>
              {image?.name || "Loading..."}
            </p>
            <div className="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">
              <span className="truncate">{image?.size ? sizeToTxt(image.size) : "Calculating..."}</span>
            </div>
            {hasError && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400" title={image!.errorMsg.join(", ")}>
                {image!.errorMsg.join(", ")}
              </p>
            )}
          </div>
        </div>
        <div className="ml-4 flex flex-shrink-0 items-center gap-2">
          {status === "inprog" && (
            <div className="w-24">
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-full rounded-full bg-blue-600 transition-all duration-300 ease-in-out"
                  style={{ width: `${prog}%` }}
                  role="progressbar"
                  aria-valuenow={prog}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center">{prog}%</p>
            </div>
          )}
          <button
            type="button"
            onClick={handleDelete}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <span className="sr-only">Delete</span>
            <FaTrash className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </li>
  );
}

export default HookFormImageUpload;
